import { useEffect, useState, type ReactNode } from 'react';
import { Building2, Loader2, Mail, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import {
  IDIOMAS, NOMBRE_IDIOMA, NOMBRE_ORIGEN, ORIGENES_SUSCRIPTOR,
  type Audiencia, type Canal, type Recuento,
} from '@/lib/comunicaciones';

/**
 * Paso 1: a quién. Dos audiencias que se pueden sumar; un email que esté en
 * las dos recibe un solo correo. El recuento lo hace el servidor con la misma
 * regla que el envío (sin bajas, sin duplicados).
 */
export default function PasoDestinatarios({ audiencia, onChange, canal = 'email' }: { audiencia: Audiencia; onChange: (a: Audiencia) => void; canal?: Canal }) {
  const whatsapp = canal === 'whatsapp';
  const [recuento, setRecuento] = useState<Recuento | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clave = JSON.stringify(audiencia);
  useEffect(() => {
    let vigente = true;
    setCargando(true);
    const t = setTimeout(() => {
      api.recuentoAudiencia(audiencia, canal)
        .then(r => { if (vigente) { setRecuento(r); setError(null); } })
        .catch(err => { if (vigente) setError(err instanceof Error ? err.message : 'Sin recuento'); })
        .finally(() => { if (vigente) setCargando(false); });
    }, 300);
    return () => { vigente = false; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const s = audiencia.suscriptores;
  const p = audiencia.propietarios;
  const alternar = <T,>(lista: T[], v: T) => (lista.includes(v) ? lista.filter(x => x !== v) : [...lista, v]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <Tarjeta
          icono={Mail}
          titulo="Suscriptores"
          texto={whatsapp
            ? 'Los suscriptores confirmados que marcaron también la casilla de WhatsApp: es un permiso aparte, y sin él no se les escribe por ahí.'
            : 'Los que se apuntaron en el portal del huésped, el check-in o la web y confirmaron por correo. Es la única lista con consentimiento para publicidad.'}
          activo={s.incluir}
          onActivo={incluir => onChange({ ...audiencia, suscriptores: { ...s, incluir } })}
        >
          <Filtro titulo="Origen" vacio="Todos">
            {ORIGENES_SUSCRIPTOR.map(o => (
              <Chip key={o} activo={s.origenes.includes(o)} onClick={() => onChange({ ...audiencia, suscriptores: { ...s, origenes: alternar(s.origenes, o) } })}>
                {NOMBRE_ORIGEN[o]}
              </Chip>
            ))}
          </Filtro>
          <Filtro titulo="Idioma" vacio="Todos">
            {IDIOMAS.map(i => (
              <Chip key={i} activo={s.idiomas.includes(i)} onClick={() => onChange({ ...audiencia, suscriptores: { ...s, idiomas: alternar(s.idiomas, i) } })}>
                {NOMBRE_IDIOMA[i]}
              </Chip>
            ))}
          </Filtro>
        </Tarjeta>

        {whatsapp ? (
          <div className="card text-sm text-sereno-dark/50 flex items-start gap-2">
            <Building2 size={16} className="mt-0.5 shrink-0" />
            Los propietarios no dieron permiso para recibir campañas por WhatsApp, así que no pueden entrar en esta.
          </div>
        ) : (
        <Tarjeta
          icono={Building2}
          titulo="Propietarios"
          texto="Para comunicados de servicio: temporada, cambios, novedades de Sereno. No es base para publicidad de terceros."
          activo={p.incluir}
          onActivo={incluir => onChange({ ...audiencia, propietarios: { ...p, incluir } })}
        >
          <Filtro titulo="Estado">
            <Chip activo={p.estados.includes('active')} onClick={() => onChange({ ...audiencia, propietarios: { ...p, estados: alternar(p.estados, 'active') } })}>Activos</Chip>
            <Chip activo={p.estados.includes('inactive')} onClick={() => onChange({ ...audiencia, propietarios: { ...p, estados: alternar(p.estados, 'inactive') } })}>Inactivos</Chip>
          </Filtro>
          <label className="flex items-center gap-2 text-sm text-sereno-dark/70 mt-2">
            <input type="checkbox" checked={p.incluirSecundario} onChange={e => onChange({ ...audiencia, propietarios: { ...p, incluirSecundario: e.target.checked } })} />
            Mandar también al email secundario
          </label>
        </Tarjeta>
        )}
      </div>

      <div className="card h-fit lg:sticky lg:top-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-sereno-dark mb-3">
          <Users size={16} /> Destinatarios {cargando && <Loader2 size={14} className="animate-spin text-sereno-dark/40" />}
        </div>
        {error && <div className="text-xs text-red-600">{error}</div>}
        {recuento && (
          <>
            <div className="text-4xl font-bold text-sereno-dark tabular-nums">{recuento.total}</div>
            <div className="text-xs text-sereno-dark/40 mb-4">personas distintas recibirán {whatsapp ? 'el WhatsApp' : 'el correo'}</div>
            <Linea etiqueta="Suscriptores" valor={recuento.porTipo.suscriptor || 0} />
            <Linea etiqueta="Propietarios" valor={recuento.porTipo.propietario || 0} />
            <div className="border-t border-gray-100 my-3" />
            {Object.entries(recuento.porIdioma).sort((a, b) => b[1] - a[1]).map(([idioma, n]) => (
              <Linea key={idioma} etiqueta={NOMBRE_IDIOMA[idioma as keyof typeof NOMBRE_IDIOMA] || idioma.toUpperCase()} valor={n} />
            ))}
            {(recuento.descartes.baja + recuento.descartes.duplicado + recuento.descartes.sinEmail) > 0 && (
              <>
                <div className="border-t border-gray-100 my-3" />
                <div className="text-xs text-sereno-dark/40 mb-1">Se quedan fuera</div>
                {recuento.descartes.baja > 0 && <Linea etiqueta="Dados de baja" valor={recuento.descartes.baja} suave />}
                {recuento.descartes.duplicado > 0 && <Linea etiqueta="Repetidos (se manda uno)" valor={recuento.descartes.duplicado} suave />}
                {recuento.descartes.sinEmail > 0 && <Linea etiqueta={whatsapp ? 'Sin teléfono válido' : 'Sin email válido'} valor={recuento.descartes.sinEmail} suave />}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Tarjeta({ icono: Icono, titulo, texto, activo, onActivo, children }: {
  icono: React.ElementType; titulo: string; texto: string; activo: boolean; onActivo: (v: boolean) => void; children: ReactNode;
}) {
  return (
    <div className={classNames('card transition-opacity', !activo && 'opacity-70')}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" className="mt-1" aria-label={`Incluir ${titulo.toLowerCase()}`} checked={activo} onChange={e => onActivo(e.target.checked)} />
        <div className="flex-1">
          <div className="flex items-center gap-2 font-semibold text-sereno-dark"><Icono size={16} /> {titulo}</div>
          <p className="text-sm text-sereno-dark/50 mt-0.5">{texto}</p>
        </div>
      </label>
      {activo && <div className="mt-4 pl-7 space-y-3">{children}</div>}
    </div>
  );
}

function Filtro({ titulo, vacio, children }: { titulo: string; vacio?: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-sereno-dark/40 mb-1.5">{titulo}{vacio && <span> · sin marcar = {vacio.toLowerCase()}</span>}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        'text-xs px-2.5 py-1 rounded-full border transition-colors',
        activo ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60 hover:border-slate-300',
      )}
    >
      {children}
    </button>
  );
}

function Linea({ etiqueta, valor, suave }: { etiqueta: string; valor: number; suave?: boolean }) {
  return (
    <div className={classNames('flex justify-between text-sm py-0.5', suave ? 'text-sereno-dark/40' : 'text-sereno-dark/70')}>
      <span>{etiqueta}</span>
      <span className="tabular-nums font-medium">{valor}</span>
    </div>
  );
}
